import { zodCustomErrorsConst } from "@/resources";
import { Company } from "./company";
import { z } from "zod/v4";

export type GeoLocation = z.infer<typeof zodGeoLocation>;

export type GeoLocationWithRadius = z.infer<typeof zodGeoLocationWithRadius>;

export const zodGeoLocation = z.object({
  longitude: z.number(),
  latitude: z.number(),
});

export const zodGeoLocationWithRadius = z.object({
  location: zodGeoLocation,
  radius: z.number().min(1).max(150),
});

export const defaultGeoLocation: GeoLocation = {
  longitude: NaN,
  latitude: NaN,
};

export interface ContactInfo {
  contactName?: string;
  email: string;
  fax?: string;
  cellphone?: string;
  telephone?: string;
  website?: string;
}

export type AddressFields = z.infer<typeof zodAddressFields>;

export const zodAddressFields = z.object({
  street: z.string().refine((value) => !!value, {
    params: { i18n: zodCustomErrorsConst.AddressStreet },
  }),
  number: z
    .string()
    .min(1)
    .refine((value) => !!value, {
      params: { i18n: zodCustomErrorsConst.AddressNumber },
    }),
  line2: z.string().optional(),
  city: z.string().refine((value) => !!value, {
    params: { i18n: zodCustomErrorsConst.AddressCity },
  }),
  state: z.string().optional(),
  postal_code: z.string().refine((value) => !!value, {
    params: { i18n: zodCustomErrorsConst.AddressPostalCode },
  }),
  country: z.string().refine((value) => !!value, {
    params: { i18n: zodCustomErrorsConst.AddressCountry },
  }),
  countryISO3166Code: z
    .string()
    .min(2)
    .max(2)
    .refine((v) => !!v, {
      params: { i18n: zodCustomErrorsConst.AddressCountry },
    }),
});

export const zodBusRequestAddressFields = z.object({
  street: z.string(),
  number: z.string(),
  line2: z.string(),
  city: z.string().refine((value) => !!value, {
    params: { i18n: zodCustomErrorsConst.AddressCity },
  }),
  state: z.string(),
  postal_code: z.string(),
  country: z.string().refine((value) => !!value, {
    params: { i18n: zodCustomErrorsConst.AddressCountry },
  }),
  countryISO3166Code: z
    .string()
    .min(2)
    .max(2)
    .refine((v) => !!v, {
      params: { i18n: zodCustomErrorsConst.AddressCountry },
    }),
});

export type BusRequestStartEndAddressFields = z.infer<
  typeof zodBusRequestAddressFields
>;

export const defaultAddress: AddressFields = {
  city: "",
  country: "",
  countryISO3166Code: "",
  number: "",
  street: "",
  state: "",
  postal_code: "",
  line2: "",
};

export type GoogleMapsAddressSpecifics = {
  placeId: string;
  types?: string[];
};

export type AddressWithLocation = GoogleMapsAddressSpecifics & {
  address: string;
  address_fields: AddressFields;
  location: GeoLocation;
};

export const zodAddressWithLocation = z.object({
  address: z.string(),
  placeId: z.string(),
  address_fields: zodAddressFields,
  location: zodGeoLocation,
  types: z.array(z.string().min(2)).optional(),
});

export type NewAddressWithLocation = z.infer<typeof zodAddressWithLocation>;

export const zodBusRequestAddressWithLocation = z.object({
  address: z.string(),
  placeId: z.string(),
  address_fields: zodBusRequestAddressFields,
  location: zodGeoLocation,
  types: z.array(z.string().min(2)).optional(),
});

export type BusrequestAddressWithLocation = z.infer<
  typeof zodBusRequestAddressWithLocation
>;

export const defaultAddressWithLocation: NewAddressWithLocation = {
  address: "",
  placeId: "",
  types: [],
  address_fields: defaultAddress,
  location: defaultGeoLocation,
};

export interface AdvertiserLocation {
  id: string;
  travelService: string;
  address: AddressWithLocation;
  // Fall back to company contact info if not provided
  contact: ContactInfo;
  disabled: boolean;
  isMainLocation: boolean;
}

export type AdvertiserLocationWithCompany = {
  company: Company<"travelService">;
  advertiserLocation: AdvertiserLocation;
};
